import React, { useCallback, useContext } from 'react';
import { connect } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';

import { makeGetTask } from 'selectors/tasks';

import { updateTask } from 'actions/tasks';

import { useCanUser } from 'shared-hooks/permission';

import { PublicPageContext } from 'context/PublicPageContext';

import TaskName from 'components/task-name';
import TaskCompleteWrap from 'components/TaskCompleteWrap';
import TaskActions from 'components/TaskActions';
import MultiLineInput from 'components/MultiLineInput';
import TaskAssignees from 'components/TaskAssignees';
import TaskDueDate from 'components/TaskDueDate';
import CanUser from 'components/can-user';
import LabelPicker from 'components/label-picker';
import LabelsList from 'components/labels-list';
import DescriptionPane from 'components/description-pane';
import TaskNiceId from 'components/TaskNiceId';
import TaskChecklists from 'components/TaskChecklists';

import IconClose from 'icons/icon-close';
import IconArrowLeft from 'icons/icon-arrow-left';
import IconMoreHoriz from 'icons/icon-more-horiz';
import IconUser from 'icons/icon-user';
import IconCalendarSimple from 'icons/icon-calendar-simple';
import IconLabel from 'icons/icon-label';

const SubtaskSingle = ({ id, name, assignees, dueDate, taskLabels, taskDescription, createdBy, niceId, projectId, discussionElement, isSmallScreen, onClose, onDelete }) => {
    const { t } = useTranslation(['task', 'labels', 'common']);
    const canEdit = useCanUser('editTask', [createdBy]);
    const canAssign = useCanUser('assignToTask', [createdBy]);
    const publicPageCtx = useContext(PublicPageContext);
    const isPublic = publicPageCtx?.isPublic === true;

    const handleCompleteToggle = useCallback(completed => {
        return updateTask(id, { completed });
    }, [id]);

    const handleNameBlur = useCallback(name => {
        updateTask(id, { name });
    }, [id]);

    const handleDueDateChange = useCallback(({ nextDate, nextStartDate }) => {
        updateTask(id, {
            due_date: nextDate,
            start_date: nextStartDate
        });
    }, [id]);

    const handleDescriptionChange = useCallback(description => {
        updateTask(id, { description });
    }, [id]);

    return (
        <div className="content-panel-main">
            <div className="content-panel-head">
                <div className="content-panel-subview-head">
                    <button className="content-panel-control btn content-panel-back" onClick={onClose}><IconArrowLeft size="20px" /></button>

                    <div className="content-panel-subview-head-main">
                        <span className="nice-id-holder">
                            <TaskNiceId niceId={niceId} />
                        </span>
                    </div>

                    <div className="content-panel-controls">
                        <div className="content-panel-simple-actions">
                            <div className="content-panel-control">
                                <TaskActions
                                    id={id} 
                                    className="content-panel-actions-trigger"
                                    innerClassName="content-panel-actions-trigger"
                                    onRemove={onDelete}
                                />
                            </div>

                            <button className="content-panel-control btn content-panel-close" onClick={onClose}><IconClose /></button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-panel-title">
                <div className="content-panel-subview-title-holder">
                    <CanUser type="completeSubtask">
                        <TaskCompleteWrap
                            id={id}
                            dark={true}
                            size="large"
                            onChange={handleCompleteToggle}
                        />
                    </CanUser>

                    <div className="content-panel-subview-title">
                        <MultiLineInput
                            className="content-panel-field-input"
                            placeholder={t('Add subtask name')}
                            canEdit={canEdit}
                            value={name}
                            onBlur={handleNameBlur}
                        />
                    </div>
                </div>
            </div>

            <div className="content-panel-scrollable nice-scroll">
                <div className="content-panel-block">
                    <DescriptionPane
                        key={id}
                        editable={canEdit}
                        placeholder={t('Add subtask description')}
                        content={taskDescription || ''}
                        onChange={handleDescriptionChange}
                    />
                </div>

                <div className="content-panel-block">
                    <div className="content-panel-utils">
                        {
                            publicPageCtx?.pageConfig?.tasks?.show_assignees !== false && (assignees?.length || canAssign) ? (
                                <div className="control">
                                    <div className="control-inner">
                                        <div className="control-label">
                                            <span className="control-icon">
                                                <IconUser />
                                            </span>

                                            <span className="control-text">
                                                { t('common:Assignees') }
                                            </span>
                                        </div>

                                        <div className="control-value">
                                            <TaskAssignees
                                                id={id}
                                                maxUsersCount={8}
                                                addUserText={ !assignees?.length ? t('common:Add') : undefined }
                                                addBtnClassName={'action-text' + (assignees?.length ? ' with-icon' : '')}
                                                withTooltip={false}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : null
                        }

                        <div className="control">
                            <div className="control-inner">
                                <div className="control-label">
                                    <span className="control-icon">
                                        <IconLabel />
                                    </span>

                                    <span className="control-text">
                                        { t('labels:Tags') }
                                    </span>
                                </div>


                                <CanUser type="editTask" customList={createdBy}>
                                    <LabelPicker
                                        taskId={id}
                                        assignedLabels={taskLabels}
                                        xAlign="left"
                                        defaultXPosition="right"
                                    >
                                        <div className="control-value">
                                            <LabelsList labels={taskLabels} inverted={true}>
                                                <span className="action-text">
                                                    { t('common:Add') }
                                                </span>
                                            </LabelsList>
                                        </div>
                                    </LabelPicker>
                                </CanUser>

                                <CanUser type="editTask" customList={createdBy} reverse={true}>
                                    <LabelsList labels={taskLabels} inverted={true} />
                                </CanUser>
                            </div>
                        </div>

                        {
                            (!dueDate && !canEdit) ? null : (
                                <div className="control">
                                    <div className="control-inner">
                                        <div className="control-label">
                                            <span className="control-icon">
                                                <IconCalendarSimple />
                                            </span>

                                            <span className="control-text">
                                                { t('common:Due Date') }
                                            </span>
                                        </div>

                                        <div className="control-value">
                                            <TaskDueDate
                                                id={id}
                                                withFromNow={true}
                                                dateFormat="fullDateTime"
                                                xAlign={isSmallScreen ? 'left' : undefined}
                                                defaultXPosition={isSmallScreen ? 'center' : undefined}
                                                showQuickOptions={!isSmallScreen}
                                                onChange={handleDueDateChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                    </div>
                </div>
                <div className="content-panel-nav-holder" />

                <div className="content-panel-body">
                    <div className="content-panel-body-main">
                        <TaskChecklists id={id} />

                        { discussionElement }
                    </div>
                </div>
            </div>
        </div>
    );
}

const makeMapStateToProps = () => {
    const getTask = makeGetTask();

    return ({ tasks }, ownProps) => {
        const task = getTask(tasks.items, ownProps.id);
        return {
            name: task?.name,
            assignees: task?.assignees,
            dueDate: task?.due_date,
            taskLabels: task?.labels,
            taskDescription: task?.description,
            createdBy: task?.created_by,
            niceId: task?.nice_id,
        }
    };
}

export default connect(makeMapStateToProps)(SubtaskSingle);
